import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import styled from "styled-components";
import {
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalDescription,
    StandardModalFormGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    portalColors,
    Toast
} from "src/components";

const SortOptionGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
    }
`;

const SortOption = styled.label`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-height: 3.5rem;
    padding: 0.875rem 1rem;
    border: 1px solid ${p => p.$selected ? portalColors.blueDark : portalColors.border};
    border-radius: 8px;
    background: ${p => p.$selected ? portalColors.blueSoft : portalColors.surface};
    color: ${portalColors.text};
    cursor: pointer;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.9375rem;
    font-weight: ${p => p.$selected ? 700 : 500};
    box-sizing: border-box;

    &:hover {
        border-color: ${portalColors.blueDark};
    }
`;

const SortOptionInput = styled.input.attrs({ type: "radio" })`
    width: 1rem;
    height: 1rem;
    margin: 0;
    accent-color: ${portalColors.blueDark};
    flex: 0 0 auto;
`;

const SortModal = ({ handleCloseSortModal, sortOption, handleSaveSort, user }) => {

    const [tempSortOption, setTempSortOption] = useState(sortOption);

    useEffect(() => {
        setTempSortOption(sortOption);
    }, [sortOption]);

    // Fetch sort option when modal opens
    useEffect(() => {
        const fetchSortOption = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/businessContacts/mobile`,
                    {
                        headers: {
                            Authorization: "Bearer " + user.token
                        }
                    }
                );
                setTempSortOption(data?.directorySortOption || "name"); // Default to "name" if there is not assigned value from db.
            } catch (error) {
                console.error(error);
                setTempSortOption("name"); // Default to "name" if fetch fails.
            }
        };
        fetchSortOption();
    }, [user.token]);

    const handleSave = async () => {
        try {
            await axios.put(`${API_URL}/businessContacts/directory/updateSortOption`,
                { sortOption: tempSortOption },
                {
                    headers: {
                        Authorization: "Bearer " + user.token,
                        "Content-Type": "application/json"
                    }
                }
            );
            Toast.success("Sort option saved successfully.");
            handleSaveSort(tempSortOption);
            handleCloseSortModal();
        } catch (error) {
            console.error(error);
            Toast.error("Failed to save sort option.");
        }
    };

    const sortMap = {
        name: "Person's Name",
        company: "Company Name",
        position: "Title/Position",
        department: "Department",
        location: "Country/Location"
    };

    return (
        <StandardModal
            open={true}
            onClose={handleCloseSortModal}
            title="Filter Alphabetically By (A-Z)"
            description="Choose which listing field users will use to browse the Directory alphabetically."
            widthMode="medium"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={handleCloseSortModal}>
                        Cancel
                    </StandardModalActionButton>
                    <StandardModalActionButton type="button" $variant="primary" onClick={handleSave}>
                        Save Filter
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <StandardModalFormGroup>
                    <StandardModalSectionTitle>Directory Listing Order</StandardModalSectionTitle>
                    <StandardModalDescription>
                        The selected field will control the alphabetical order displayed to all app users.
                    </StandardModalDescription>
                    <SortOptionGrid role="radiogroup" aria-label="Directory alphabetical filter">
                        {Object.entries(sortMap).map(([key, label]) => (
                            <SortOption key={key} $selected={tempSortOption === key}>
                                <SortOptionInput
                                    name="sort"
                                    value={key}
                                    checked={tempSortOption === key}
                                    onChange={event => setTempSortOption(event.target.value)}
                                />
                                {label}
                            </SortOption>
                        ))}
                    </SortOptionGrid>
                </StandardModalFormGroup>
            </StandardModalSection>
        </StandardModal>
    );
};

export default SortModal;
